/**
 * Slug-ID utilities
 *
 * Generate and parse slug-IDs in format: {kebab-case-name}-{id}
 * Example: "acme-marketing-7x9k2"
 */

import type { SlugIdResult } from "./types";

/**
 * Generate slug-ID from name and ID
 * Example: generateSlugId("Acme Marketing", "7x9k2") => "acme-marketing-7x9k2"
 */
export function generateSlugId(name: string, id: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 50); // Max 50 chars for name part

  return `${slug}-${id}`;
}

/**
 * Parse slug-ID to extract ID
 * Handles both short IDs and UUIDs
 * Examples:
 *   parseSlugId("acme-marketing-7x9k2") => { id: "7x9k2" }
 *   parseSlugId("test-5487ab17-c5c3-4f6c-abbf-a8c64268604b") => { id: "5487ab17-c5c3-4f6c-abbf-a8c64268604b" }
 */
export function parseSlugId(slugId: string): SlugIdResult {
  // UUID regex pattern: 8-4-4-4-12 hex digits
  const uuidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  
  // First check if there's a UUID at the end
  const uuidMatch = slugId.match(uuidRegex);
  if (uuidMatch) {
    return { id: uuidMatch[0] };
  }
  
  // Fall back to original behavior for short IDs (after last dash)
  const parts = slugId.split("-");
  const id = parts[parts.length - 1];
  return { id };
}
